"use client";

import { useEffect } from "react";
import { trackEvent } from "@/lib/analytics";

/**
 * Erfasst Klicks auf jedes Element mit `data-cta-location` als `cta_clicked`.
 *
 * Ein globaler Listener statt `onClick` an jedem Knopf, weil zwei Orte keinen
 * React-Handler haben koennen: Storyblok-Buttons sind Server-Komponenten, und
 * CTAs im Artikeltext werden per `renderToStaticMarkup` gerendert
 * (Article.tsx) — ein `onClick` dort wird nie hydriert.
 *
 * Capture-Phase, damit der Klick gezaehlt ist, bevor ein Handler das Element
 * aushaengt (z. B. der Knopf im Event-Modal, der das Modal schliesst).
 *
 * Attribute:
 * - `data-cta-location` (Pflicht): Ort, z. B. `header`, `event_hero`
 * - `data-cta-label`: Knopftext; ohne ihn der sichtbare Text
 * - `data-cta-external`: zusaetzlich `affiliation_link_clicked` senden
 */
export function CtaClickTracker() {
  useEffect(() => {
    // Im Storyblok Visual Editor waehlen Redakteure Bloecke per Klick aus —
    // das sind keine Nutzerklicks.
    const inEditor = () => window.location.search.includes("_storyblok");

    const report = (el: HTMLElement) => {
      // GA kuerzt Parameter ueber 100 Zeichen; der Textinhalt als Fallback
      // koennte mehr enthalten als den Knopftext.
      const label =
        (el.dataset.ctaLabel || el.textContent?.trim() || "").slice(0, 100) || null;
      const href = el.getAttribute("href");

      trackEvent("cta_clicked", {
        location: el.dataset.ctaLocation,
        label,
        href,
        page_path: window.location.pathname,
      });

      if (el.dataset.ctaExternal !== undefined) {
        trackEvent("affiliation_link_clicked", { label, url: href });
      }
    };

    const handleClick = (e: MouseEvent) => {
      if (inEditor()) return;
      const el = (e.target as Element | null)?.closest<HTMLElement>("[data-cta-location]");
      if (el) report(el);
    };

    // `div role="button"` (AuthModalButton, MembershipPaymentButton) reagiert
    // per onKeyDown auf Enter/Leertaste, ohne dass ein click-Event entsteht.
    // Native Buttons und Links loesen dabei selbst einen Klick aus und
    // laufen ueber handleClick.
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      if (inEditor()) return;
      const el = e.target as HTMLElement | null;
      if (el?.getAttribute("role") === "button" && el.dataset.ctaLocation) report(el);
    };

    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);

  return null;
}
